import React, { useCallback, useId } from 'react'

import {
  Button, Icon, InfoOutlined, Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import { PLAN_ANNUAL } from 'Pods/SubscriptionCheckout/Constants/plans'
import BillingTerm from 'Pods/SubscriptionCheckout/Shared/BillingTerm/BillingTerm'
import LegalAgreement from 'Pods/SubscriptionCheckout/Shared/LegalAgreement/LegalAgreement'
import PlanCard from 'Pods/SubscriptionCheckout/Shared/PlanCard/PlanCard'

import formatPrice from 'Utils/formatPrice'
import { NOOP } from 'Utils/stableDefaults'

import { quoteShape } from '../../propTypes'
import termsForTargetTier from '../../Utils/quoteTerms'
import ExplainerBanner from '../ExplainerBanner/ExplainerBanner'
import LegacyPlanCard from './Components/LegacyPlanCard/LegacyPlanCard'

const TRANSLATION_KEY_PREFIX = 'subscription_upgrade.order_summary'
const RESTRUCTURE_KEY_PREFIX = 'subscription_checkout.plans_restructure.upgrade'
const RESTRUCTURE_SUMMARY_KEY_PREFIX = 'subscription_checkout.plans_restructure.order_summary'

function ctaKeyFor({ current, targetPlan }) {
  if (current.tier === targetPlan.tier) return `cta.switch_to_annual.${current.tier}`
  return `cta.upgrade.${targetPlan.planType}`
}

function OrderSummary({
  premiumPrice,
  discount = 0,
  downloadsUsed,
  unusedCredits,
  amountDueNow,
  vatAmount = null,
  premiumPlan = null,
  quote = null,
  isQuoteLoading = false,
  onTargetTermChange = NOOP,
  onPay = NOOP,
  disabled = false,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const vatTooltipId = useId()

  const subtotal = quote ? quote.targetPlan.price : premiumPrice
  const credit = quote ? quote.credit.amount : discount
  const vat = quote ? quote.vatAmount : vatAmount
  const amountDue = quote ? quote.amountDue : amountDueNow
  const terms = quote ? termsForTargetTier(quote) : []
  const targetTerm = quote && terms.find((term) => term.key === quote.targetPlan.planType)
  const renewalKey = quote?.targetPlan.planType === PLAN_ANNUAL ? 'renews_at' : 'next_billing'

  const subtotalLabel = quote
    ? translate(`${RESTRUCTURE_KEY_PREFIX}.subtotal.${quote.targetPlan.tier}`)
    : translate(`${TRANSLATION_KEY_PREFIX}.premium_price`)
  const creditLabel = quote
    ? translate(`${RESTRUCTURE_KEY_PREFIX}.credit.${quote.current.tier}.${quote.credit.planType}`)
    : translate(`${TRANSLATION_KEY_PREFIX}.credit_label`)
  const creditBasis = quote
    ? translate(`${RESTRUCTURE_KEY_PREFIX}.credit_basis.${quote.credit.planType}`, {
      unused: quote.units.total - quote.units.used,
      total: quote.units.total,
    })
    : translate(`${TRANSLATION_KEY_PREFIX}.credit_basis`, {
      unused: unusedCredits,
      limit: unusedCredits + downloadsUsed,
    })
  const ctaLabel = quote
    ? translate(`${RESTRUCTURE_KEY_PREFIX}.${ctaKeyFor(quote)}`, { amount: formatPrice(amountDue) })
    : translate(`${TRANSLATION_KEY_PREFIX}.cta`, { amount: formatPrice(amountDue) })

  const legacyLegal = (
    <>
      {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_prefix`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_terms`)}
      </a>
      {' '}{translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_connector`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_privacy`)}
      </a>
      .
    </>
  )

  return (
    <div className="order-summary">
      <h2 className="order-summary__title cgt-heading-300">
        {translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      </h2>

      <ExplainerBanner downloadsUsed={downloadsUsed} unusedCredits={unusedCredits} quote={quote} locale={locale} />

      {quote ? (
        <>
          <PlanCard
            tier={quote.targetPlan.tier}
            plan={targetTerm}
            catalogValue={quote.catalogValue}
            locale={locale}
          />
          <BillingTerm
            plans={terms}
            selectedKey={quote.targetPlan.planType}
            onChange={onTargetTermChange}
            isChangeDisabled={isQuoteLoading}
            locale={locale}
          />
        </>
      ) : premiumPlan && <LegacyPlanCard premiumPlan={premiumPlan} locale={locale} />}

      <hr className="order-summary__divider" />

      <div className="order-summary__totals">
        <div className="order-summary__row order-summary__row--premium-price">
          <span className={quote ? 'cgt-body-base' : 'cgt-body-base-semibold'}>{subtotalLabel}</span>
          <span className="cgt-body-base">{formatPrice(subtotal)}</span>
        </div>

        <div className="order-summary__row order-summary__row--credit">
          <span className="order-summary__credit-label cgt-body-base">
            {creditLabel}
            <span className="order-summary__credit-basis cgt-caption">{creditBasis}</span>
          </span>
          <span className="order-summary__credit-amount cgt-body-base">
            {`-${formatPrice(credit)}`}
          </span>
        </div>

        {vat > 0 && (
          <div className="order-summary__row order-summary__row--vat">
            <span className="order-summary__row-label--with-info cgt-body-base">
              {translate(`${TRANSLATION_KEY_PREFIX}.vat_included`)}
              <Tooltip content={translate(`${TRANSLATION_KEY_PREFIX}.vat_tooltip`)} id={vatTooltipId}>
                <span className="order-summary__info-icon">
                  <Icon size="small" svgComponent={InfoOutlined} color="dark" />
                </span>
              </Tooltip>
            </span>
            <span className="cgt-body-base">{formatPrice(vat)}</span>
          </div>
        )}
      </div>

      <hr className="order-summary__divider" />

      <div className="order-summary__total-row">
        <span className="cgt-heading-50">
          {translate(`${TRANSLATION_KEY_PREFIX}.amount_due_today`)}
        </span>
        <span className="order-summary__total-amount cgt-heading-200">
          {formatPrice(amountDue)}
        </span>
      </div>

      <Button
        type="primary"
        expand
        actionType="button"
        customClass="order-summary__pay-button"
        onClick={onPay}
        disabled={disabled || isQuoteLoading}
      >
        {ctaLabel}
      </Button>

      <div className="order-summary__legal cgt-body-base">
        {quote ? <LegalAgreement withFairUse locale={locale} /> : legacyLegal}
      </div>

      <p className="order-summary__footer cgt-caption">
        {quote
          ? translate(`${RESTRUCTURE_SUMMARY_KEY_PREFIX}.${renewalKey}`, { price: formatPrice(subtotal) })
          : translate(`${TRANSLATION_KEY_PREFIX}.next_billing_note`, { price: formatPrice(premiumPrice) })}
      </p>
    </div>
  )
}

OrderSummary.propTypes = {
  premiumPrice: number.isRequired,
  discount: number,
  downloadsUsed: number.isRequired,
  unusedCredits: number.isRequired,
  amountDueNow: number.isRequired,
  vatAmount: number,
  premiumPlan: shape({
    features: arrayOf(string),
    originalPrice: number,
    currentPrice: number,
    priceUnit: string,
  }),
  quote: quoteShape,
  isQuoteLoading: bool,
  onTargetTermChange: func,
  onPay: func,
  disabled: bool,
  locale: string,
}

export default OrderSummary
